import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/toaster';
import { TopNavigation, AppLayout } from '@/components/layout';
import { TrajectoryList } from '@/pages/TrajectoryList';
import { TrajectoryView } from '@/pages/TrajectoryView';
import { AgentList, AgentDetail } from '@/components/agents';
import { EvalList, EvalDetail, EvalRunDetail, ScheduleList } from '@/components/evals';
import { EditorPage } from '@/components/editor';
import { PlaygroundPage } from '@/pages/PlaygroundPage';
import { IDEPage } from '@/pages/IDEPage';
import { SchemasPage } from '@/pages/SchemasPage';
import { SchemaEditorPage } from '@/pages/SchemaEditorPage';
import { DeveloperPage } from '@/pages/DeveloperPage';
import { FunctionsListPage } from '@/pages/FunctionsListPage';
import { FunctionEditorPage } from '@/pages/FunctionEditorPage';
import { SchedulesPage } from '@/pages/SchedulesPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { OrganizationPage } from '@/components/organization';
import { AdminPage } from '@/components/admin';
import { GlobalAssistantProvider, GlobalAssistantRouteListener } from '@/contexts/GlobalAssistantContext';
import { GlobalAssistantPanel } from '@/components/assistant';
import { LoginPage, OnboardingPage, AuthGuard } from '@/components/auth';
import { OnboardingWizard } from '@/components/onboarding';
import { InvitePage } from '@/pages/InvitePage';
import { UserProfilePage } from '@/pages/UserProfilePage';
import { MarketingPage } from '@/pages/MarketingPage';
import NotebooksListPage from '@/pages/NotebooksListPage';
import NotebookPage from '@/pages/NotebookPage';
import { AgentsPage } from '@/pages/AgentsPage';
import { AgentWizardPage } from '@/pages/AgentWizardPage';
import { AgentIdentityDetailPage } from '@/pages/AgentIdentityDetailPage';
import { MonitoringPage } from '@/pages/MonitoringPage';
import { SSOAdminPage } from '@/pages/SSOAdminPage';
import { EmbedManagementPage } from '@/pages/EmbedManagementPage';
import { KnowledgePage } from '@/pages/KnowledgePage';

function App() {
  return (
    <TooltipProvider>
      <GlobalAssistantProvider>
        <Router>
          <GlobalAssistantRouteListener />
          <GlobalAssistantPanel />
          <AppLayout>
          <Routes>
          {/* Auth routes (public) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/invite/:code" element={<InvitePage />} />
          <Route path="/onboarding" element={
            <AuthGuard requireGroup={false}>
              <OnboardingWizard />
            </AuthGuard>
          } />

          {/* Marketing page (public) */}
          <Route path="/" element={<MarketingPage />} />

          {/* Authenticated home - redirects to trajectories */}
          <Route path="/home" element={
            <AuthGuard>
              <Navigate to="/trajectories" replace />
            </AuthGuard>
          } />

          {/* Trajectories */}
          <Route
            path="/trajectories"
            element={
              <AuthGuard>
                <TopNavigation />
                <TrajectoryList />
              </AuthGuard>
            }
          />
          <Route
            path="/trajectory/:trajectoryId"
            element={
              <AuthGuard>
                <TrajectoryView />
              </AuthGuard>
            }
          />

          {/* Agents */}
          <Route
            path="/agents"
            element={
              <AuthGuard>
                <TopNavigation />
                <AgentsPage />
              </AuthGuard>
            }
          />
          <Route
            path="/agents/:agentId"
            element={
              <AuthGuard>
                <TopNavigation />
                <AgentDetail />
              </AuthGuard>
            }
          />
          <Route
            path="/agents/wizard"
            element={
              <AuthGuard>
                <AgentWizardPage />
              </AuthGuard>
            }
          />
          <Route
            path="/agents/identities/:identityId"
            element={
              <AuthGuard>
                <TopNavigation />
                <AgentIdentityDetailPage />
              </AuthGuard>
            }
          />

          {/* Evals */}
          <Route
            path="/evals"
            element={
              <AuthGuard>
                <TopNavigation />
                <EvalList />
              </AuthGuard>
            }
          />
          {/* Redirect old eval schedules to new schedules page */}
          <Route path="/evals/schedules" element={<Navigate to="/schedules" replace />} />
          {/* Redirect old eval functions to developer functions */}
          <Route path="/evals/functions" element={<Navigate to="/developer/functions" replace />} />
          <Route path="/evals/functions/:functionId" element={<Navigate to="/developer/functions" replace />} />
          <Route
            path="/evals/:evalId"
            element={
              <AuthGuard>
                <TopNavigation />
                <EvalDetail />
              </AuthGuard>
            }
          />
          <Route
            path="/evals/:evalId/runs/:runId"
            element={
              <AuthGuard>
                <TopNavigation />
                <EvalRunDetail />
              </AuthGuard>
            }
          />

          {/* Developer */}
          <Route
            path="/developer"
            element={
              <AuthGuard>
                <TopNavigation />
                <DeveloperPage />
              </AuthGuard>
            }
          />
          <Route
            path="/developer/functions"
            element={
              <AuthGuard>
                <TopNavigation />
                <FunctionsListPage />
              </AuthGuard>
            }
          />
          <Route
            path="/developer/functions/:id"
            element={
              <AuthGuard>
                <TopNavigation />
                <FunctionEditorPage />
              </AuthGuard>
            }
          />
          <Route
            path="/developer/schemas"
            element={
              <AuthGuard>
                <TopNavigation />
                <SchemasPage />
              </AuthGuard>
            }
          />
          <Route
            path="/developer/schemas/:schemaId"
            element={
              <AuthGuard>
                <TopNavigation />
                <SchemaEditorPage />
              </AuthGuard>
            }
          />

          {/* Developer - Editor */}
          <Route
            path="/developer/editor"
            element={
              <AuthGuard>
                <TopNavigation />
                <EditorPage />
              </AuthGuard>
            }
          />
          {/* Developer - Playground */}
          <Route
            path="/developer/playground"
            element={
              <AuthGuard>
                <TopNavigation />
                <PlaygroundPage />
              </AuthGuard>
            }
          />
          {/* Developer - IDE (unified editor + playground) */}
          <Route
            path="/developer/ide"
            element={
              <AuthGuard>
                <TopNavigation />
                <IDEPage />
              </AuthGuard>
            }
          />
          {/* Developer - SSO Admin */}
          <Route
            path="/developer/sso"
            element={
              <AuthGuard>
                <TopNavigation />
                <SSOAdminPage />
              </AuthGuard>
            }
          />
          {/* Developer - Embed Management */}
          <Route
            path="/developer/embed"
            element={
              <AuthGuard>
                <TopNavigation />
                <EmbedManagementPage />
              </AuthGuard>
            }
          />
          {/* Developer - Knowledge Base */}
          <Route
            path="/developer/knowledge"
            element={
              <AuthGuard>
                <TopNavigation />
                <KnowledgePage />
              </AuthGuard>
            }
          />
          {/* Redirects for old routes */}
          <Route path="/editor" element={<Navigate to="/developer/ide" replace />} />
          <Route path="/playground" element={<Navigate to="/developer/ide" replace />} />

          {/* Monitoring & Feedback */}
          <Route
            path="/monitoring"
            element={
              <AuthGuard>
                <TopNavigation />
                <MonitoringPage />
              </AuthGuard>
            }
          />

          {/* Schedules */}
          <Route
            path="/schedules"
            element={
              <AuthGuard>
                <TopNavigation />
                <SchedulesPage />
              </AuthGuard>
            }
          />

          {/* Notebooks */}
          <Route
            path="/notebooks"
            element={
              <AuthGuard>
                <TopNavigation />
                <NotebooksListPage />
              </AuthGuard>
            }
          />
          <Route
            path="/notebooks/:notebookId"
            element={
              <AuthGuard>
                <TopNavigation />
                <NotebookPage />
              </AuthGuard>
            }
          />

          {/* Profile */}
          <Route
            path="/profile"
            element={
              <AuthGuard requireGroup={false}>
                <TopNavigation />
                <UserProfilePage />
              </AuthGuard>
            }
          />

          {/* Settings */}
          <Route
            path="/settings"
            element={
              <AuthGuard requireGroup={false}>
                <SettingsPage />
              </AuthGuard>
            }
          />

          {/* Organization Management */}
          <Route
            path="/organization"
            element={
              <AuthGuard>
                <OrganizationPage />
              </AuthGuard>
            }
          />

          {/* Admin (Superadmin only) */}
          <Route
            path="/admin"
            element={
              <AuthGuard>
                <AdminPage />
              </AuthGuard>
            }
          />

          {/* 404 */}
          <Route
            path="*"
            element={
              <>
                <TopNavigation />
                <div className="min-h-[calc(100vh-56px)] bg-background flex items-center justify-center">
                  <div className="text-center">
                    <h1 className="text-4xl font-bold mb-2">404</h1>
                    <p className="text-muted-foreground">Page not found</p>
                  </div>
                </div>
              </>
            }
          />
          </Routes>
          </AppLayout>
        </Router>
        <Toaster />
      </GlobalAssistantProvider>
    </TooltipProvider>
  );
}

export default App;
